/** Study-guide quiz banks for the Multivariable Geometry of Surfaces module — 20 MCQs per topic (6 Easy / 8 Medium / 6 Hard). */

// Surface Metrics and the First Fundamental Form
export const SG_METRIC_QUIZ = [
  {
    prompt: "For a regular parametrization $\\mathbf r(u,v)$, the coefficient $E$ of the first fundamental form is",
    options: [
      "$\\mathbf r_u\\cdot\\mathbf r_v$",
      "$\\mathbf r_u\\cdot\\mathbf r_u$",
      "$|\\mathbf r_u\\times\\mathbf r_v|$",
      "$\\mathbf r_v\\cdot\\mathbf r_v$",
    ],
    answer: "B",
    explanation: "By definition $E=\\mathbf r_u\\cdot\\mathbf r_u$, $F=\\mathbf r_u\\cdot\\mathbf r_v$ and $G=\\mathbf r_v\\cdot\\mathbf r_v$. So $E=|\\mathbf r_u|^2$ is the squared speed along the $u$-coordinate curves.",
    id: "sg-metric-01",
    difficulty: "Easy",
  },
  {
    prompt: "A parametrization $\\mathbf r(u,v)$ is regular at $(u_0,v_0)$ exactly when",
    options: [
      "$\\mathbf r_u\\cdot\\mathbf r_v=0$",
      "$\\mathbf r_u\\times\\mathbf r_v\\neq\\mathbf 0$",
      "$E=G$",
      "$\\mathbf r_u=\\mathbf r_v$",
    ],
    answer: "B",
    explanation: "Regularity means $\\mathbf r_u$ and $\\mathbf r_v$ are linearly independent, which is equivalent to $\\mathbf r_u\\times\\mathbf r_v\\neq\\mathbf 0$. Orthogonality ($F=0$) is a stronger, optional property.",
    id: "sg-metric-02",
    difficulty: "Easy",
  },
  {
    prompt: "For the plane $\\mathbf r(u,v)=(u,v,0)$, the first fundamental form is",
    options: [
      "$du^2+2\\,du\\,dv+dv^2$",
      "$u^2\\,du^2+dv^2$",
      "$du^2+dv^2$",
      "$2\\,du\\,dv$",
    ],
    answer: "C",
    explanation: "$\\mathbf r_u=(1,0,0)$ and $\\mathbf r_v=(0,1,0)$ give $E=1$, $F=0$, $G=1$, so $ds^2=du^2+dv^2$ — the ordinary Euclidean metric.",
    id: "sg-metric-03",
    difficulty: "Easy",
  },
  {
    prompt: "The $u$- and $v$-coordinate curves meet at right angles at every point exactly when",
    options: [
      "$F\\equiv 0$",
      "$E=G$",
      "$EG-F^2=1$",
      "$E\\equiv 0$",
    ],
    answer: "A",
    explanation: "The angle $\\omega$ between coordinate curves satisfies $\\cos\\omega=F/\\sqrt{EG}$, so $\\omega=\\pi/2$ everywhere iff $F\\equiv0$ (an orthogonal parametrization). $E\\equiv0$ is impossible at a regular point.",
    id: "sg-metric-04",
    difficulty: "Easy",
  },
  {
    prompt: "In terms of $E,F,G$, the surface area element is",
    options: [
      "$(E+G)\\,du\\,dv$",
      "$\\sqrt{EG-F^2}\\,du\\,dv$",
      "$\\sqrt{E+G}\\,du\\,dv$",
      "$(EG-F^2)\\,du\\,dv$",
    ],
    answer: "B",
    explanation: "$dA=|\\mathbf r_u\\times\\mathbf r_v|\\,du\\,dv$, and by Lagrange's identity $|\\mathbf r_u\\times\\mathbf r_v|^2=EG-F^2$. Forgetting the square root (last option) is a common slip.",
    id: "sg-metric-05",
    difficulty: "Easy",
  },
  {
    prompt: "For the graph surface $\\mathbf r(x,y)=(x,y,f(x,y))$, the coefficient $E$ equals",
    options: [
      "$f_x^2$",
      "$1+f_xf_y$",
      "$1+f_y^2$",
      "$1+f_x^2$",
    ],
    answer: "D",
    explanation: "$\\mathbf r_x=(1,0,f_x)$, so $E=\\mathbf r_x\\cdot\\mathbf r_x=1+f_x^2$. Similarly $F=f_xf_y$ and $G=1+f_y^2$.",
    id: "sg-metric-06",
    difficulty: "Easy",
  },
  {
    prompt: "For the sphere $\\mathbf r(\\varphi,\\theta)=(R\\sin\\varphi\\cos\\theta,\\;R\\sin\\varphi\\sin\\theta,\\;R\\cos\\varphi)$, the coefficient $G=\\mathbf r_\\theta\\cdot\\mathbf r_\\theta$ is",
    options: [
      "$R^2$",
      "$R^2\\sin^2\\varphi$",
      "$R\\sin\\varphi$",
      "$R^2\\cos^2\\varphi$",
    ],
    answer: "B",
    explanation: "$\\mathbf r_\\theta=(-R\\sin\\varphi\\sin\\theta,\\;R\\sin\\varphi\\cos\\theta,\\;0)$, so $|\\mathbf r_\\theta|^2=R^2\\sin^2\\varphi$. Meanwhile $E=|\\mathbf r_\\varphi|^2=R^2$ and $F=0$.",
    id: "sg-metric-07",
    difficulty: "Medium",
  },
  {
    prompt: "On the sphere of radius $R=2$ (coordinates as in the guide), what is the length of the latitude circle $\\varphi=\\pi/3$, $0\\le\\theta\\le2\\pi$?",
    options: [
      "$4\\pi$",
      "$\\pi\\sqrt3$",
      "$2\\pi\\sqrt3$",
      "$2\\pi$",
    ],
    answer: "C",
    explanation: "Along $\\varphi=\\pi/3$, $\\varphi'=0$ and $\\theta'=1$, so $L=\\int_0^{2\\pi}\\sqrt{G}\\,d\\theta=2\\pi R\\sin(\\pi/3)=2\\pi\\cdot2\\cdot\\tfrac{\\sqrt3}{2}=2\\pi\\sqrt3$. $4\\pi$ is the equator's length.",
    id: "sg-metric-08",
    difficulty: "Medium",
  },
  {
    prompt: "On the paraboloid $z=x^2+y^2$, parametrized as a graph, what is the cosine of the angle between the $x$- and $y$-coordinate curves at $(x,y)=(1,1)$?",
    options: [
      "$0$",
      "$4/5$",
      "$4/25$",
      "$3/5$",
    ],
    answer: "B",
    explanation: "$E=1+4x^2=5$, $F=4xy=4$, $G=1+4y^2=5$ at $(1,1)$. Then $\\cos\\omega=F/\\sqrt{EG}=4/5$. The coordinate curves are not perpendicular on the surface even though the $x$- and $y$-axes are perpendicular in the parameter plane.",
    id: "sg-metric-09",
    difficulty: "Medium",
  },
  {
    prompt: "On the cylinder $\\mathbf r(u,v)=(\\cos u,\\sin u,v)$, what is the length of the curve $u=t$, $v=2t$, $0\\le t\\le 2\\pi$?",
    options: [
      "$6\\pi$",
      "$2\\pi\\sqrt3$",
      "$4\\pi$",
      "$2\\pi\\sqrt5$",
    ],
    answer: "D",
    explanation: "The cylinder has $E=1$, $F=0$, $G=1$. With $u'=1$, $v'=2$: $L=\\int_0^{2\\pi}\\sqrt{1+4}\\,dt=2\\pi\\sqrt5$. Unrolled, this helix is a straight segment with legs $2\\pi$ and $4\\pi$.",
    id: "sg-metric-10",
    difficulty: "Medium",
  },
  {
    prompt: "For a surface of revolution $\\mathbf r(u,v)=(f(v)\\cos u,\\;f(v)\\sin u,\\;g(v))$ with $f>0$, which statement is true?",
    options: [
      "$F=0$, so parallels and meridians always meet at right angles",
      "$F=f(v)f'(v)$",
      "$E=1$ for every profile curve",
      "$G=f(v)^2$",
    ],
    answer: "A",
    explanation: "$\\mathbf r_u=(-f\\sin u,f\\cos u,0)$ and $\\mathbf r_v=(f'\\cos u,f'\\sin u,g')$ give $F=-ff'\\sin u\\cos u+ff'\\sin u\\cos u=0$. Also $E=f^2$ and $G=f'^2+g'^2$.",
    id: "sg-metric-11",
    difficulty: "Medium",
  },
  {
    prompt: "Why is $EG-F^2>0$ at every regular point?",
    options: [
      "Because $E=G$ at a regular point",
      "Because $F$ is always zero",
      "Lagrange's identity gives $EG-F^2=|\\mathbf r_u\\times\\mathbf r_v|^2$, which is positive when $\\mathbf r_u\\times\\mathbf r_v\\neq\\mathbf 0$",
      "Because $\\mathbf r_u$ and $\\mathbf r_v$ are unit vectors",
    ],
    answer: "C",
    explanation: "$|\\mathbf a\\times\\mathbf b|^2=|\\mathbf a|^2|\\mathbf b|^2-(\\mathbf a\\cdot\\mathbf b)^2$. With $\\mathbf a=\\mathbf r_u$, $\\mathbf b=\\mathbf r_v$ this is $EG-F^2$, positive exactly at regular points. None of the other conditions is required.",
    id: "sg-metric-12",
    difficulty: "Medium",
  },
  {
    prompt: "The plane in polar coordinates, $\\mathbf r(u,v)=(v\\cos u,v\\sin u,0)$, has $E=v^2$, $F=0$, $G=1$. What happens at $v=0$?",
    options: [
      "The plane has a cusp at the origin",
      "The parametrization fails to be regular, but the plane itself is smooth there: a coordinate singularity",
      "The tangent plane at the origin is vertical",
      "The first fundamental form is undefined because $E<0$",
    ],
    answer: "B",
    explanation: "At $v=0$, $\\mathbf r_u=\\mathbf 0$, so $EG-F^2=v^2=0$ and the parametrization is not regular. The Cartesian parametrization $(u,v,0)$ is regular there, so the defect belongs to the coordinates, not the surface.",
    id: "sg-metric-13",
    difficulty: "Medium",
  },
  {
    prompt: "On the unit sphere at $\\varphi=\\pi/4$ (so $E=1$, $F=0$, $G=\\tfrac12$), let $\\mathbf w_1=\\mathbf r_\\varphi+\\mathbf r_\\theta$ and $\\mathbf w_2=\\mathbf r_\\varphi-\\mathbf r_\\theta$. The cosine of the angle between them is",
    options: [
      "$0$",
      "$1/2$",
      "$1/3$",
      "$-1/3$",
    ],
    answer: "C",
    explanation: "$\\langle\\mathbf w_1,\\mathbf w_2\\rangle=E-G=\\tfrac12$ and $I(\\mathbf w_1)=I(\\mathbf w_2)=E+G=\\tfrac32$. So $\\cos\\theta=\\tfrac{1/2}{3/2}=\\tfrac13$. The directions $(1,1)$ and $(1,-1)$ look perpendicular in the parameter plane, but not on the surface.",
    id: "sg-metric-14",
    difficulty: "Medium",
  },
  {
    prompt: "Let $\\tilde{\\mathbf r}(s,t)=\\mathbf r(2s,t)$. In terms of the coefficients $E,F,G$ of $\\mathbf r$ (evaluated at $(2s,t)$), the new coefficients $(\\tilde E,\\tilde F,\\tilde G)$ are",
    options: [
      "$(2E,\\,2F,\\,G)$",
      "$(4E,\\,4F,\\,G)$",
      "$(E,\\,F,\\,G)$",
      "$(4E,\\,2F,\\,G)$",
    ],
    answer: "D",
    explanation: "By the chain rule $\\tilde{\\mathbf r}_s=2\\mathbf r_u$ and $\\tilde{\\mathbf r}_t=\\mathbf r_v$. Hence $\\tilde E=4E$, $\\tilde F=2F$, $\\tilde G=G$, which matches $\\tilde g=J^{T}gJ$ with $J=\\mathrm{diag}(2,1)$. The coefficients depend on the parametrization.",
    id: "sg-metric-15",
    difficulty: "Hard",
  },
  {
    prompt: "For the cone $\\mathbf r(u,v)=(v\\cos u,\\;v\\sin u,\\;v)$, $v\\ge0$, the quantity $EG-F^2$ equals",
    options: [
      "$2v^2$",
      "$v^2$",
      "$2$",
      "$2v$",
    ],
    answer: "A",
    explanation: "$\\mathbf r_u=(-v\\sin u,v\\cos u,0)$ and $\\mathbf r_v=(\\cos u,\\sin u,1)$ give $E=v^2$, $F=0$, $G=2$, so $EG-F^2=2v^2$. It vanishes at the apex, where the cone genuinely has no tangent plane.",
    id: "sg-metric-16",
    difficulty: "Hard",
  },
  {
    prompt: "At a point where $E=1$, $F=1$, $G=3$, the tangent vector $\\mathbf w=2\\mathbf r_u-\\mathbf r_v$ has length",
    options: [
      "$\\sqrt5$",
      "$\\sqrt3$",
      "$\\sqrt{11}$",
      "$3$",
    ],
    answer: "B",
    explanation: "$I(a\\mathbf r_u+b\\mathbf r_v)=Ea^2+2Fab+Gb^2=4+2(2)(-1)(1)+3=3$, so $|\\mathbf w|=\\sqrt3$. $\\sqrt5$ comes from dropping the factor $2$ on $F$; $\\sqrt{11}$ from a sign error; $3$ forgets the square root.",
    id: "sg-metric-17",
    difficulty: "Hard",
  },
  {
    prompt: "The helicoid $\\mathbf r(u,v)=(v\\cos u,\\;v\\sin u,\\;u)$ has $E=v^2+1$, $F=0$, $G=1$. The area of the piece $0\\le u\\le2\\pi$, $0\\le v\\le1$ is",
    options: [
      "$2\\pi\\sqrt2$",
      "$\\pi(1+\\sqrt2)$",
      "$\\pi\\bigl(\\sqrt2+\\ln(1+\\sqrt2)\\bigr)$",
      "$2\\pi$",
    ],
    answer: "C",
    explanation: "$A=\\int_0^{2\\pi}\\!\\int_0^1\\sqrt{v^2+1}\\,dv\\,du=2\\pi\\cdot\\tfrac12\\bigl[v\\sqrt{v^2+1}+\\ln(v+\\sqrt{v^2+1})\\bigr]_0^1=\\pi\\bigl(\\sqrt2+\\ln(1+\\sqrt2)\\bigr)\\approx7.21$.",
    id: "sg-metric-18",
    difficulty: "Hard",
  },
  {
    prompt: "Which triple $(E,F,G)$ cannot be the first fundamental form coefficients of a regular parametrization at a point?",
    options: [
      "$(1,0,1)$",
      "$(2,1,1)$",
      "$(5,4,5)$",
      "$(1,2,3)$",
    ],
    answer: "D",
    explanation: "At a regular point the matrix $\\begin{pmatrix}E&F\\\\F&G\\end{pmatrix}$ must be positive definite: $E>0$ and $EG-F^2>0$. For $(1,2,3)$, $EG-F^2=3-4=-1<0$. The others give $1$, $1$ and $9$.",
    id: "sg-metric-19",
    difficulty: "Hard",
  },
  {
    prompt: "Which quantity is not determined by the first fundamental form alone?",
    options: [
      "The length of a curve on the surface",
      "The angle between two curves on the surface",
      "The area of a region of the surface",
      "The mean curvature (how the surface bends in space)",
    ],
    answer: "D",
    explanation: "Lengths, angles and areas are all computed from $E,F,G$. Mean curvature is extrinsic: the plane and the cylinder share the metric $du^2+dv^2$ but have mean curvature $0$ and $\\tfrac12$ respectively. It needs the second fundamental form (next topic).",
    id: "sg-metric-20",
    difficulty: "Hard",
  },
];

// Surface Curvature and Geodesics
export const SG_CURVATURE_QUIZ = [
  {
    prompt: "The coefficient $L$ of the second fundamental form is",
    options: [
      "$\\mathbf r_u\\cdot\\mathbf r_u$",
      "$\\mathbf r_{uu}\\cdot\\mathbf n$",
      "$\\mathbf r_{uv}\\cdot\\mathbf n$",
      "$\\mathbf r_{uu}\\cdot\\mathbf r_u$",
    ],
    answer: "B",
    explanation: "$L=\\mathbf r_{uu}\\cdot\\mathbf n$, $M=\\mathbf r_{uv}\\cdot\\mathbf n$, $N=\\mathbf r_{vv}\\cdot\\mathbf n$: the normal components of the second derivatives. Dot products of first derivatives give the first fundamental form instead.",
    id: "sg-curv-01",
    difficulty: "Easy",
  },
  {
    prompt: "In terms of the principal curvatures $k_1,k_2$, the Gaussian curvature is",
    options: [
      "$k_1+k_2$",
      "$\\tfrac12(k_1+k_2)$",
      "$k_1k_2$",
      "$k_1-k_2$",
    ],
    answer: "C",
    explanation: "$K=k_1k_2=\\det S$, the determinant of the shape operator. The average $\\tfrac12(k_1+k_2)$ is the mean curvature $H$.",
    id: "sg-curv-02",
    difficulty: "Easy",
  },
  {
    prompt: "In terms of the principal curvatures $k_1,k_2$, the mean curvature is",
    options: [
      "$\\tfrac12(k_1+k_2)$",
      "$k_1k_2$",
      "$k_1-k_2$",
      "$\\sqrt{k_1k_2}$",
    ],
    answer: "A",
    explanation: "$H=\\tfrac12(k_1+k_2)=\\tfrac12\\operatorname{tr}S$, the average of the largest and smallest normal curvatures.",
    id: "sg-curv-03",
    difficulty: "Easy",
  },
  {
    prompt: "The Gaussian curvature of a sphere of radius $R$ is",
    options: [
      "$1/R$",
      "$R^2$",
      "$0$",
      "$1/R^2$",
    ],
    answer: "D",
    explanation: "Every normal section is a great circle of radius $R$, so $k_1=k_2=\\pm1/R$ and $K=k_1k_2=1/R^2$ for either choice of normal.",
    id: "sg-curv-04",
    difficulty: "Easy",
  },
  {
    prompt: "The geodesics of a plane are",
    options: [
      "Circles",
      "Straight lines",
      "Parabolas",
      "Helices",
    ],
    answer: "B",
    explanation: "A geodesic has acceleration normal to the surface. In a plane the acceleration also lies in the plane, so it must be zero: the curve is a straight line traversed at constant speed.",
    id: "sg-curv-05",
    difficulty: "Easy",
  },
  {
    prompt: "A point of a surface where $K<0$ is called",
    options: [
      "Elliptic",
      "Parabolic",
      "Umbilic",
      "Hyperbolic (saddle-shaped)",
    ],
    answer: "D",
    explanation: "$K<0$ means $k_1$ and $k_2$ have opposite signs, so the surface bends up in one direction and down in another, like a saddle. $K>0$ is elliptic and $K=0$ (not planar) is parabolic.",
    id: "sg-curv-06",
    difficulty: "Easy",
  },
  {
    prompt: "For a circular cylinder of radius $a$, the Gaussian curvature is",
    options: [
      "$0$",
      "$1/a$",
      "$1/a^2$",
      "$-1/a^2$",
    ],
    answer: "A",
    explanation: "The principal curvatures are $\\pm1/a$ (around the cylinder) and $0$ (along a ruling), so $K=0$. This agrees with the cylinder being locally isometric to the plane.",
    id: "sg-curv-07",
    difficulty: "Medium",
  },
  {
    prompt: "For the graph $z=f(x,y)$ at a point where $f_x=f_y=0$, the Gaussian curvature equals",
    options: [
      "$f_{xx}+f_{yy}$",
      "$f_xf_y$",
      "$1+f_x^2+f_y^2$",
      "$f_{xx}f_{yy}-f_{xy}^2$",
    ],
    answer: "D",
    explanation: "In general $K=\\dfrac{f_{xx}f_{yy}-f_{xy}^2}{(1+f_x^2+f_y^2)^2}$. At a critical point the denominator is $1$, so $K$ is the Hessian discriminant $D$ from the second-derivative test.",
    id: "sg-curv-08",
    difficulty: "Medium",
  },
  {
    prompt: "For the saddle $z=xy$, the Gaussian curvature at the origin is",
    options: [
      "$1$",
      "$0$",
      "$-1$",
      "$-2$",
    ],
    answer: "C",
    explanation: "At the origin $f_x=f_y=0$, $f_{xx}=f_{yy}=0$, $f_{xy}=1$, so $K=0\\cdot0-1^2=-1$. Also $H=0$ there.",
    id: "sg-curv-09",
    difficulty: "Medium",
  },
  {
    prompt: "Which circles of latitude $\\varphi=\\varphi_0$ on a sphere are geodesics?",
    options: [
      "Only the equator $\\varphi_0=\\pi/2$",
      "Every latitude",
      "None of them",
      "Only latitudes close to the poles",
    ],
    answer: "A",
    explanation: "For a latitude, $\\boldsymbol\\alpha''\\cdot\\mathbf r_\\varphi=-R^2\\sin\\varphi_0\\cos\\varphi_0$, a tangential component that vanishes only when $\\cos\\varphi_0=0$. Only the equator is a great circle.",
    id: "sg-curv-10",
    difficulty: "Medium",
  },
  {
    prompt: "If the unit normal $\\mathbf n$ is replaced by $-\\mathbf n$, then",
    options: [
      "Both $K$ and $H$ change sign",
      "$K$ changes sign but $H$ does not",
      "$H$ changes sign but $K$ does not",
      "Neither changes",
    ],
    answer: "C",
    explanation: "$L,M,N$ all change sign. $K=\\dfrac{LN-M^2}{EG-F^2}$ is unchanged because it is quadratic in them, while $H$ is linear in them and flips sign.",
    id: "sg-curv-11",
    difficulty: "Medium",
  },
  {
    prompt: "At a point with principal curvatures $k_1=3$ and $k_2=-1$, the normal curvature in the direction making angle $\\theta=\\pi/4$ with the first principal direction is",
    options: [
      "$2$",
      "$0$",
      "$-1$",
      "$1$",
    ],
    answer: "D",
    explanation: "Euler's formula: $\\kappa_n=k_1\\cos^2\\theta+k_2\\sin^2\\theta=3\\cdot\\tfrac12+(-1)\\cdot\\tfrac12=1$.",
    id: "sg-curv-12",
    difficulty: "Medium",
  },
  {
    prompt: "Is the helix $\\boldsymbol\\alpha(t)=(a\\cos t,\\,a\\sin t,\\,ct)$ a geodesic of the cylinder $x^2+y^2=a^2$?",
    options: [
      "No, because a helix has nonzero curvature",
      "Yes: its acceleration $(-a\\cos t,-a\\sin t,0)$ is normal to the cylinder",
      "Only when $c=0$",
      "Only when $a=1$",
    ],
    answer: "B",
    explanation: "The speed is constant and the acceleration $-a(\\cos t,\\sin t,0)$ is a multiple of the normal $(\\cos t,\\sin t,0)$, so the tangential part is zero. Curving in space is allowed; only curving within the surface is not.",
    id: "sg-curv-13",
    difficulty: "Medium",
  },
  {
    prompt: "For the paraboloid $z=x^2+y^2$ with upward normal, the curvatures at the origin are",
    options: [
      "$K=2,\\;H=4$",
      "$K=4,\\;H=4$",
      "$K=4,\\;H=2$",
      "$K=2,\\;H=2$",
    ],
    answer: "C",
    explanation: "At the origin $E=G=1$, $F=0$, $\\mathbf n=(0,0,1)$, $L=N=2$, $M=0$. So $k_1=k_2=2$, $K=4$ and $H=2$; the origin is an umbilic point.",
    id: "sg-curv-14",
    difficulty: "Medium",
  },
  {
    prompt: "At a point where $E=1,\\;F=0,\\;G=4$ and $L=2,\\;M=1,\\;N=3$, the Gaussian curvature is",
    options: [
      "$5$",
      "$5/4$",
      "$5/16$",
      "$5/2$",
    ],
    answer: "B",
    explanation: "$K=\\dfrac{LN-M^2}{EG-F^2}=\\dfrac{6-1}{4-0}=\\dfrac54$. Forgetting to divide by $EG-F^2$ gives the distractor $5$.",
    id: "sg-curv-15",
    difficulty: "Hard",
  },
  {
    prompt: "For the torus $\\mathbf r=\\bigl((a+b\\cos v)\\cos u,\\,(a+b\\cos v)\\sin u,\\,b\\sin v\\bigr)$ with $a>b>0$, $K=\\dfrac{\\cos v}{b(a+b\\cos v)}$. The curvature on the inner equator $v=\\pi$ is",
    options: [
      "$-\\dfrac{1}{b(a-b)}$",
      "$0$",
      "$\\dfrac{1}{b(a+b)}$",
      "$-\\dfrac{1}{a^2}$",
    ],
    answer: "A",
    explanation: "At $v=\\pi$, $\\cos v=-1$ and $a+b\\cos v=a-b$, giving $K=-\\dfrac{1}{b(a-b)}<0$: the inner side is saddle-shaped. The outer equator $v=0$ has $K=\\dfrac{1}{b(a+b)}>0$.",
    id: "sg-curv-16",
    difficulty: "Hard",
  },
  {
    prompt: "Two points on a sphere of radius $R$ both lie at latitude $60^\\circ$N, with longitudes $180^\\circ$ apart. The great-circle distance between them is",
    options: [
      "$\\pi R/2$",
      "$\\pi R/6$",
      "$2\\pi R/3$",
      "$\\pi R/3$",
    ],
    answer: "D",
    explanation: "The great circle runs over the North Pole, covering $30^\\circ+30^\\circ=60^\\circ=\\pi/3$ of arc, so the distance is $\\pi R/3$. Following the latitude circle instead gives $\\pi R\\cos60^\\circ=\\pi R/2$, which is longer.",
    id: "sg-curv-17",
    difficulty: "Hard",
  },
  {
    prompt: "Why can no flat map of a region of the Earth preserve all distances exactly?",
    options: [
      "By Gauss's Theorema Egregium, $K$ is intrinsic; the sphere has $K=1/R^2$ and the plane has $K=0$, so no isometry exists",
      "Because the sphere is not a regular surface",
      "Because the first fundamental form of the sphere has $F\\neq0$",
      "Because the sphere's mean curvature is zero",
    ],
    answer: "A",
    explanation: "A distance-preserving map would preserve $E,F,G$ and hence the intrinsic $K$. Since $1/R^2\\neq0$, every map projection must distort some lengths, angles or areas.",
    id: "sg-curv-18",
    difficulty: "Hard",
  },
  {
    prompt: "On the unit sphere, the longer great-circle arc joining two points at angular distance $\\pi/2$ has length $3\\pi/2$. This arc is",
    options: [
      "Not a geodesic, because it is not the shortest path",
      "A geodesic, but not length-minimising",
      "A geodesic and the shortest path",
      "Not a curve that lies on the sphere",
    ],
    answer: "B",
    explanation: "Every arc of a great circle has acceleration normal to the sphere, so it is a geodesic. Geodesics are only locally shortest; once an arc passes the antipodal point it is no longer minimising.",
    id: "sg-curv-19",
    difficulty: "Hard",
  },
  {
    prompt: "Which of these quantities is intrinsic (determined by $E,F,G$ and their derivatives alone)?",
    options: [
      "The mean curvature $H$",
      "The principal curvature $k_1$",
      "The Gaussian curvature $K$",
      "The unit normal $\\mathbf n$",
    ],
    answer: "C",
    explanation: "The plane and the cylinder share the metric $du^2+dv^2$ but have $H=0$ and $H=\\mp\\tfrac{1}{2a}$, and different $k_1$; only $K$ (zero for both) agrees. This is the Theorema Egregium.",
    id: "sg-curv-20",
    difficulty: "Hard",
  },
];
